import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../services/api";
import { useAuthStore } from "../stores/authStore";
import type { ParentRole } from "@starquest/core";
import styles from "./JoinFamily.module.css";

type Step = "loading" | "invalid" | "email" | "code";

export default function JoinFamily() {
  const { inviteCode } = useParams<{ inviteCode: string }>();
  const navigate = useNavigate();
  const setAuth  = useAuthStore((s) => s.setAuth);

  const [step,       setStep]       = useState<Step>("loading");
  const [familyName, setFamilyName] = useState("");
  const [expiresAt,  setExpiresAt]  = useState("");
  const [email,      setEmail]      = useState("");
  const [code,       setCode]       = useState("");
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState("");

  useEffect(() => {
    if (!inviteCode) { setStep("invalid"); return; }
    api.getInvite(inviteCode).then((res) => {
      if (!res.valid) { setStep("invalid"); return; }
      setFamilyName(res.familyName ?? "");
      setExpiresAt(res.expiresAt ? new Date(res.expiresAt).toLocaleDateString() : "");
      setStep("email");
    }).catch(() => setStep("invalid"));
  }, [inviteCode]);

  async function handleSendCode(e: React.FormEvent) {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await api.sendOtp(email.trim().toLowerCase());
      setStep("code");
    } catch { setError("Failed to send code. Check your email and try again."); }
    finally { setLoading(false); }
  }

  async function handleJoin(e: React.FormEvent) {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const res = await api.verifyOtpWithInvite(email.trim().toLowerCase(), code.trim(), inviteCode!);
      const payload = JSON.parse(atob(res.token.split(".")[1]));
      setAuth(res.token, res.familyId!, email, payload.parentRole as ParentRole);
      navigate("/parent");
    } catch { setError("Invalid code or invite already used."); }
    finally { setLoading(false); }
  }

  if (step === "loading") return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.icon}>⌛</div>
        <div className={styles.title}>Checking invite…</div>
      </div>
    </div>
  );

  if (step === "invalid") return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.icon}>❌</div>
        <div className={styles.title}>Invite not valid</div>
        <div className={`${styles.sub} ${styles.invalid}`}>This invite link has expired or already been used.</div>
        <button className={`${styles.btn} ${styles.primary}`} onClick={() => navigate("/login")}>Go to Login</button>
      </div>
    </div>
  );

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.icon}>⭐</div>
        {step === "email" && (
          <>
            <div className={styles.title}>You're invited!</div>
            <div className={styles.family}>{familyName}</div>
            <div className={styles.expiry}>Invite expires {expiresAt}</div>
            <div className={styles.sub}>Enter your email to join as a Co-Master</div>
            <form onSubmit={handleSendCode}>
              <input
                className={styles.input}
                type="email" required autoFocus
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              {error && <div className={styles.error}>{error}</div>}
              <button type="submit" className={`${styles.btn} ${styles.primary}`} disabled={loading || !email.trim()}>
                {loading ? "Sending…" : "Send Code"}
              </button>
            </form>
          </>
        )}
        {step === "code" && (
          <>
            <div className={styles.title}>Check your email</div>
            <div className={styles.sub}>Enter the 6-digit code sent to {email}</div>
            <form onSubmit={handleJoin}>
              <input
                className={styles.input}
                type="text" inputMode="numeric" required autoFocus
                placeholder="000000" maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              />
              {error && <div className={styles.error}>{error}</div>}
              <button type="submit" className={`${styles.btn} ${styles.primary}`} disabled={loading || code.length < 6}>
                {loading ? "Joining…" : `Join ${familyName}`}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
